import { useCallback, useEffect, useMemo, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { TemplatePtAPI, type SendReceiveHistoryType, type SendReceiveRecord } from "@repo/api";
import { UserAPI } from "@repo/api/user";
import { templateKeys } from "@repo/query";

export type HistoryUserBrief = {
  nickname: string;
  avatar_url: string;
};

export type HistoryParty = {
  displayName: string;
  avatarUrl?: string;
  partyUid: string;
  templateId: string;
  sendId?: string;
};

export type UseSendReceiveHistoryOptions = {
  uid?: string;
  name?: string;
  receiver_id?: string;
  type: SendReceiveHistoryType;
  pageSize?: number;
  /** 最大展示条数；不传则不限制 */
  maxCount?: number;
  enabled?: boolean;
  onError?: (message: string) => void;
};

async function fetchUserBriefMap(records: SendReceiveRecord[]) {
  const uniqueUids = Array.from(
    new Set(records.flatMap((item) => [item.uid, item.receiver_uid]).filter(Boolean)),
  );
  if (uniqueUids.length === 0) {
    return new Map<string, HistoryUserBrief>();
  }

  try {
    const { data } = await UserAPI.batchQueryByUIDs(uniqueUids);
    return new Map(
      data.hit.map((user) => [
        user.uid,
        {
          nickname: user.nickname || user.uid,
          avatar_url: user.avatar_url || "",
        },
      ]),
    );
  } catch {
    return new Map<string, HistoryUserBrief>();
  }
}

export function resolveHistoryParty(
  item: SendReceiveRecord,
  type: SendReceiveHistoryType,
  userMap: Map<string, HistoryUserBrief>,
): HistoryParty {
  const partyUid = type === "recive" ? item.uid : item.receiver_uid;
  const userBrief = partyUid ? userMap.get(partyUid) : undefined;
  return {
    displayName: userBrief?.nickname || partyUid || "-",
    avatarUrl: userBrief?.avatar_url,
    partyUid: partyUid || "",
    templateId: String(item.template_id),
    sendId: item.send_id,
  };
}

/** 一页历史 + 这一页涉及到的用户简介（随页缓存，翻页不重复查用户） */
type HistoryPage = {
  records: SendReceiveRecord[];
  users: Map<string, HistoryUserBrief>;
  totalPages: number;
};

/**
 * 收发历史（无限滚动），web / webview 共用。
 * 切换筛选条件会换 key，旧请求结果不会串页；`maxCount` 限制总展示条数，到达后停止加载。
 * 接口返回 401 表示对方未开放查看（`shareDenied`），不当作错误 toast。
 */
export function useSendReceiveHistory({
  uid = "",
  name = "",
  receiver_id = "",
  type,
  pageSize = 20,
  maxCount,
  enabled = true,
  onError,
}: UseSendReceiveHistoryOptions) {
  const hasMaxCount = typeof maxCount === "number" && maxCount > 0;
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const query = useInfiniteQuery({
    queryKey: templateKeys.sendReceiveHistory({ type, uid, name, receiver_id, pageSize }),
    queryFn: async ({ pageParam }): Promise<HistoryPage> => {
      const { data } = await TemplatePtAPI.getSendReceiveTemplates(
        pageParam,
        pageSize,
        type,
        uid,
        name,
        receiver_id,
      );
      const records = data.content ?? [];
      const users = await fetchUserBriefMap(records);
      return { records, users, totalPages: data.total_pages || 0 };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      const nextPage = pages.length + 1;
      if (lastPage.totalPages > 0) {
        return pages.length < lastPage.totalPages ? nextPage : undefined;
      }
      // 接口没给总页数时，按「满页」判断还有没有下一页
      return lastPage.records.length >= pageSize ? nextPage : undefined;
    },
    enabled,
    // 401 是「对方未开放」的业务态，不要重试
    retry: false,
  });

  const { items, userMap } = useMemo(() => {
    const pages = query.data?.pages ?? [];
    const all = pages.flatMap((p) => p.records);
    const items = hasMaxCount ? all.slice(0, maxCount) : all;
    const userMap = new Map<string, HistoryUserBrief>();
    pages.forEach((p) => p.users.forEach((value, key) => userMap.set(key, value)));
    return { items, userMap };
  }, [query.data, hasMaxCount, maxCount]);

  const reachedLimit = hasMaxCount && items.length >= (maxCount as number);
  const errorStatus = (query.error as { status?: number } | null)?.status;
  const shareDenied = errorStatus === 401;
  const hasMore = enabled && !shareDenied && !reachedLimit && (query.hasNextPage ?? false);
  // 未启用时不请求，isPending 会一直为 true，这里按非加载态处理
  const loading = enabled && query.isPending;
  const loadingMore = query.isFetchingNextPage;

  useEffect(() => {
    if (!query.error || shareDenied) return;
    const msg = (query.error as { msg?: string }).msg;
    onErrorRef.current?.(msg || (query.data ? "加载更多失败" : "加载历史记录失败"));
  }, [query.error, shareDenied, query.data]);

  const { fetchNextPage } = query;
  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) return;
    void fetchNextPage();
  }, [hasMore, loading, loadingMore, fetchNextPage]);

  return {
    items,
    userMap,
    page: query.data?.pages.length ?? 1,
    hasMore,
    reachedLimit,
    loading,
    loadingMore,
    shareDenied,
    loadMore,
    resolveParty: (item: SendReceiveRecord) => resolveHistoryParty(item, type, userMap),
  };
}
